import { useMemo } from "react";
import { History, ShoppingBag } from "lucide-react";
import ProductImage from "./ProductImage";
import { useCart } from "../context/CartContext";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import { getProductAlt } from "../lib/productAlt";

const MIN_ITEMS_TO_SHOW = 2;

const RecentlyViewed = ({ products }) => {
  const { ids } = useRecentlyViewed();
  const { addItem } = useCart();

  const items = useMemo(() => {
    if (!products?.length || !ids?.length) return [];
    const byId = new Map(products.map((p) => [p.id, p]));
    return ids
      .map((id) => byId.get(id))
      .filter((p) => p && !p.in_store_only);
  }, [ids, products]);

  if (items.length < MIN_ITEMS_TO_SHOW) return null;

  return (
    <section
      data-testid="recently-viewed-strip"
      className="mb-10 rounded-3xl border border-white/10 bg-gradient-to-r from-[#ff1a8c]/5 via-transparent to-[#00b4ff]/5 p-6 lg:p-7"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-[#00b4ff] font-bold">
          <History className="w-3.5 h-3.5" />
          You looked at these recently
        </p>
        <span className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
          Pick up where you left off
        </span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" data-testid="recently-viewed-scroller">
        {items.map((p) => (
          <article
            key={p.id}
            data-testid={`recently-viewed-card-${p.id}`}
            className="snap-start shrink-0 w-44 sm:w-52 rounded-2xl border border-white/10 bg-black/30 overflow-hidden hover:border-[#ff1a8c]/60 transition-all"
          >
            <ProductImage
              src={p.image}
              alt={getProductAlt(p)}
              size="md"
              className="w-full h-32 object-cover"
            />
            <div className="p-3.5 flex flex-col gap-2">
              <h4 className="text-sm font-semibold text-white truncate-2 leading-tight">{p.name}</h4>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-bold neon-blue">${p.price.toFixed(2)}</span>
                <button
                  data-testid={`recently-viewed-add-${p.id}`}
                  onClick={() => addItem(p)}
                  aria-label={`Add ${p.name} to cart again`}
                  className="w-8 h-8 rounded-full border border-[#ff1a8c]/60 flex items-center justify-center text-[#ff1a8c] hover:bg-[#ff1a8c] hover:text-white transition-all"
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default RecentlyViewed;
